<script setup lang="ts">
    import { ChevronUpDownIcon, PlusIcon, BuildingLibraryIcon, UserIcon } from '@heroicons/vue/16/solid';
    import { SidebarMenuItem, SidebarMenuButton } from '@/shared/ui/Sidebar';
    import { Popover, PopoverTrigger, PopoverContent } from '@/shared/ui/Popover';
    import { Item, ItemMedia, ItemContent, ItemTitle } from '@/shared/ui/Item';
    import { Separator } from '@/shared/ui/Separator';
    import { useEntitiesStore } from '@/core/stores';
    import NewEntityDialog from '@/shared/components/dialogs/NewEntityDialog.vue';
    import { useApplicationStore } from '@/stores/application.store';

    const appStore = useApplicationStore();
    const entitiesStore = useEntitiesStore();

    const { t } = useI18n();

    const hasEntities = computed(() => !!entitiesStore.entities.length);
    const selectedEntity = computed(() => {
        if (!appStore.selectedEntityId) return;
        return entitiesStore.entities.find((e) => e.id === appStore.selectedEntityId);
    });

    const selectEntity = (id: string) => {
        console.log('Select', id);
        appStore.selectedEntityId = id;
    };
</script>

<template>
    <SidebarMenuItem class="entity-menu-item">
        <!-- Outside the popover, which unmounts its content on close -->
        <NewEntityDialog
            v-slot="{ open, triggerAttrs }"
            @created="selectEntity($event.id)"
        >
            <SidebarMenuButton
                v-if="!hasEntities"
                v-bind="triggerAttrs"
                :tooltip="t('NEW_COMPANY')"
                @click="open"
            >
                <PlusIcon />
                <span>{{ t('NEW_COMPANY') }}</span>
            </SidebarMenuButton>

            <Popover
                v-if="hasEntities"
                v-slot="{ close }"
            >
                <PopoverTrigger as-child>
                    <SidebarMenuButton
                        :style="{ padding: '2px 0px !important' }"
                        as-child
                    >
                        <div class="cursor-pointer">
                            <div class="logo size-8 rounded-lg flex items-center justify-center border border-border-subdued bg-background-primary">
                                <BuildingLibraryIcon
                                    v-if="selectedEntity?.corporation_type !== 'eenmanszaak'"
                                    class="size-4"
                                />

                                <UserIcon
                                    v-if="selectedEntity?.corporation_type === 'eenmanszaak'"
                                    class="size-4"
                                />
                            </div>
                            <span>{{ selectedEntity?.name ?? t('NOT_SELECTED') }}</span>
                            <ChevronUpDownIcon class="ml-auto text-content-subdued" />
                        </div>
                    </SidebarMenuButton>
                </PopoverTrigger>
                <PopoverContent
                    align="start"
                    class="p-2"
                >
                    <Item
                        v-for="entity in entitiesStore.entities"
                        :key="entity.name"
                        size="sm"
                        class="px-1 py-1 hover:bg-background-secondary w-full rounded-lg"
                        as="button"
                        @click="
                            selectEntity(entity.id);
                            close();
                        "
                    >
                        <ItemMedia variant="icon">
                            <BuildingLibraryIcon
                                v-if="entity.corporation_type !== 'eenmanszaak'"
                                class="size-4"
                            />

                            <UserIcon
                                v-if="entity.corporation_type === 'eenmanszaak'"
                                class="size-4"
                            />
                        </ItemMedia>
                        <ItemContent>
                            <ItemTitle class="text-sm">{{ entity.name }}</ItemTitle>
                        </ItemContent>
                    </Item>

                    <Separator class="my-2" />

                    <Item
                        size="sm"
                        class="px-1 py-1 hover:bg-background-secondary w-full rounded-lg"
                        as="button"
                        @click="
                            close();
                            open();
                        "
                    >
                        <ItemMedia
                            variant="icon"
                            class="border-none bg-transparent"
                        >
                            <PlusIcon class="size-4 text-content-subdued" />
                        </ItemMedia>
                        <ItemContent>
                            <ItemTitle class="text-sm font-medium">{{ t('NEW_COMPANY') }}</ItemTitle>
                        </ItemContent>
                    </Item>
                </PopoverContent>
            </Popover>
        </NewEntityDialog>
    </SidebarMenuItem>
</template>

<i18n lang="yaml">
en:
    NEW_COMPANY: New company
    NOT_SELECTED: Not selected
nl:
    NEW_COMPANY: Nieuw bedrijf
    NOT_SELECTED: Niet geselecteerd
</i18n>

<style lang="css" scoped>
    .entity-menu-item .logo {
        padding: 0 !important;
        min-width: 32px;
        min-height: 32px;
    }
</style>
